import React, { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries, LineSeries } from 'lightweight-charts';
import { chartTimeFromIso } from '../utils/adxChartData';
import { applyVisibleRange } from '../utils/etpListingHelpers';
import './MacdDashboard.css';

const MACD_HISTORY_DAYS = 30;
const MACD_DEFAULT_ZOOM = '1M';

// ── MACD signal helpers ───────────────────────────────────────────────────────
function macdSignal(macdLine) {
  if (macdLine == null) return { label: '—',        cls: 'macd-signal-none'    };
  if (macdLine >  1)   return { label: 'Bullish',   cls: 'macd-signal-bull'    };
  if (macdLine >  0)   return { label: 'Weak Bull', cls: 'macd-signal-weakbull'};
  if (macdLine > -1)   return { label: 'Weak Bear', cls: 'macd-signal-weakbear'};
  return                      { label: 'Bearish',   cls: 'macd-signal-bear'    };
}

// ── Chart colours ─────────────────────────────────────────────────────────────
const COLOR_MACD_LINE = '#1F4E8C';
const COLOR_ZERO      = '#4A5672';

// ── Helpers ───────────────────────────────────────────────────────────────────
function compareChartTime(a, b) {
  if (a == null || b == null) return 0;
  if (typeof a === 'string' || typeof b === 'string') {
    return String(a).localeCompare(String(b));
  }
  return a - b;
}

function dedupSort(arr) {
  const m = new Map();
  arr.forEach((p) => {
    if (p.time == null) return;
    m.set(p.time, p);
  });
  return Array.from(m.values()).sort((a, b) => compareChartTime(a.time, b.time));
}

function applySyncedViewport(priceChart, macdChart, barData, rangeLabel) {
  if (!priceChart || !macdChart || !barData?.length) return;
  try {
    applyVisibleRange(priceChart, barData, rangeLabel);
    applyVisibleRange(macdChart, barData, rangeLabel);
  } catch {
    priceChart.timeScale().fitContent();
    macdChart.timeScale().fitContent();
  }
}

// ─────────────────────────────────────────────────────────────────────────────

function MacdDashboard() {
  // ── DOM refs ────────────────────────────────────────────────────────────────
  const priceContainerRef = useRef(null);
  const macdContainerRef  = useRef(null);

  // ── Chart / series refs ─────────────────────────────────────────────────────
  const priceChartRef = useRef(null);
  const macdChartRef  = useRef(null);
  const candleRef     = useRef(null);
  const macdLineRef   = useRef(null);
  const zeroLineRef   = useRef(null);
  const syncBlockRef  = useRef(false);

  // ── State ───────────────────────────────────────────────────────────────────
  const [pair, setPair]                     = useState('');
  const [availablePairs, setAvailablePairs] = useState([]);

  const [priceLoading, setPriceLoading] = useState(true);
  const [macdLoading, setMacdLoading]   = useState(true);
  const [error, setError]               = useState(null);

  const [latestMacd, setLatestMacd] = useState(null);

  // ── Create charts once on mount ─────────────────────────────────────────────
  useEffect(() => {
    if (!priceContainerRef.current || !macdContainerRef.current) return;

    const sharedTsOpts = {
      borderColor: '#E3D9C6',
      timeVisible:    true,
      secondsVisible: false,
    };

    // Price chart (top panel)
    const priceChart = createChart(priceContainerRef.current, {
      layout:          { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid:            { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair:       { mode: 1 },
      rightPriceScale: { borderColor: '#E3D9C6' },
      timeScale:       sharedTsOpts,
      width:  priceContainerRef.current.clientWidth,
      height: priceContainerRef.current.clientHeight,
    });

    const candleSeries = priceChart.addSeries(CandlestickSeries, {
      upColor: '#1F4E8C', downColor: '#C0571F',
      borderVisible: false,
      wickUpColor: '#1F4E8C', wickDownColor: '#C0571F',
    });

    // MACD sub-chart (bottom panel) — dedicated Y-axis
    const macdChart = createChart(macdContainerRef.current, {
      layout:          { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid:            { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair:       { mode: 1 },
      rightPriceScale: {
        borderColor: '#E3D9C6',
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      timeScale: { ...sharedTsOpts, visible: false },
      width:  macdContainerRef.current.clientWidth,
      height: macdContainerRef.current.clientHeight,
    });

    const macdLine = macdChart.addSeries(LineSeries, {
      color: COLOR_MACD_LINE, lineWidth: 2,
      priceLineVisible: false, lastValueVisible: true, title: 'MACD',
    });

    const zeroLine = macdChart.addSeries(LineSeries, {
      color: COLOR_ZERO, lineWidth: 1, lineStyle: 2,
      priceLineVisible: false, lastValueVisible: false, title: '0',
    });

    priceChartRef.current = priceChart;
    macdChartRef.current  = macdChart;
    candleRef.current     = candleSeries;
    macdLineRef.current   = macdLine;
    zeroLineRef.current   = zeroLine;

    // ── Sync time scales ─────────────────────────────────────────────────────
    let syncP = false, syncM = false;
    priceChart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (syncM || syncBlockRef.current) return;
      syncP = true;
      macdChart.timeScale().setVisibleLogicalRange(range);
      syncP = false;
    });
    macdChart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (syncP || syncBlockRef.current) return;
      syncM = true;
      priceChart.timeScale().setVisibleLogicalRange(range);
      syncM = false;
    });

    // ── Resize observers ─────────────────────────────────────────────────────
    const roP = new ResizeObserver(entries => {
      for (const e of entries) priceChart.applyOptions({ width: e.contentRect.width, height: e.contentRect.height });
    });
    roP.observe(priceContainerRef.current);

    const roM = new ResizeObserver(entries => {
      for (const e of entries) macdChart.applyOptions({ width: e.contentRect.width, height: e.contentRect.height });
    });
    roM.observe(macdContainerRef.current);

    return () => {
      roP.disconnect();
      roM.disconnect();
      priceChart.remove();
      macdChart.remove();
      priceChartRef.current = macdChartRef.current = null;
      candleRef.current = macdLineRef.current = zeroLineRef.current = null;
    };
  }, []);

  // ── Fetch available pairs ───────────────────────────────────────────────────

  useEffect(() => {
    fetch('/api/config/pairs')
      .then(r => r.json())
      .then(data => {
        setAvailablePairs(data.pairs || []);
        setPair(data.default || (data.pairs && data.pairs[0]) || '');
      })
      .catch(() => setError('Failed to load trading pairs.'));
  }, []);

  // ── Fetch price + MACD (shared time axis) ───────────────────────────────────
  useEffect(() => {
    if (!pair || !candleRef.current || !macdLineRef.current) return undefined;

    let cancelled = false;
    setPriceLoading(true);
    setMacdLoading(true);
    setError(null);

    const priceUrl = `/api/ohlc-daily?pair=${encodeURIComponent(pair)}&days=${MACD_HISTORY_DAYS}`;
    const macdUrl = `/api/indicators/macd?pair=${encodeURIComponent(pair)}&days=${MACD_HISTORY_DAYS}&warmup=false`;

    Promise.all([fetch(priceUrl).then((r) => r.json()), fetch(macdUrl).then((r) => r.json())])
      .then(([priceJson, macdJson]) => {
        if (cancelled) return;

        const candles = dedupSort(
          (priceJson.data || [])
            .map((row) => {
              const time = chartTimeFromIso(row.time, 'daily');
              if (!time) return null;
              return {
                time,
                open: parseFloat(row.open),
                high: parseFloat(row.high),
                low: parseFloat(row.low),
                close: parseFloat(row.close),
              };
            })
            .filter(Boolean),
        );

        const macdData = [];
        (macdJson.data || []).forEach((row) => {
          const time = chartTimeFromIso(row.time, 'daily');
          if (time != null && row.macd_line != null) {
            macdData.push({ time, value: parseFloat(row.macd_line) });
          }
        });
        const sortedMacd = dedupSort(macdData);

        if (candleRef.current) candleRef.current.setData(candles);
        if (macdLineRef.current) macdLineRef.current.setData(sortedMacd);
        if (zeroLineRef.current && sortedMacd.length > 0) {
          zeroLineRef.current.setData(sortedMacd.map((p) => ({ time: p.time, value: 0 })));
        }

        const rows = macdJson.data || [];
        if (rows.length > 0) {
          const last = rows[rows.length - 1];
          setLatestMacd(last.macd_line != null ? parseFloat(last.macd_line) : null);
        } else {
          setLatestMacd(null);
        }

        const priceChart = priceChartRef.current;
        const macdChart = macdChartRef.current;
        if (priceChart && macdChart && candles.length > 0) {
          syncBlockRef.current = true;
          try {
            applySyncedViewport(priceChart, macdChart, candles, MACD_DEFAULT_ZOOM);
          } finally {
            syncBlockRef.current = false;
          }
        } else if (priceChart && macdChart) {
          priceChart.timeScale().fitContent();
          macdChart.timeScale().fitContent();
        }
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load MACD / price data.');
      })
      .finally(() => {
        if (!cancelled) {
          setPriceLoading(false);
          setMacdLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [pair]);

  // ── Derived values ──────────────────────────────────────────────────────────
  const signal  = macdSignal(latestMacd);
  const loading = priceLoading || macdLoading;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="macd-dashboard">

      {/* ── Controls bar ──────────────────────────────────────────────────── */}
      <div className="macd-controls">

        {/* Pair selector */}
        <label htmlFor="macd-pair-select" className="pair-label">Pair</label>
        <select
          id="macd-pair-select"
          value={pair}
          onChange={e => setPair(e.target.value)}
          className="pair-select"
          disabled={availablePairs.length === 0}
        >
          {availablePairs.map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        {/* Daily-only badge */}
        <span className="macd-range-badge">Daily · 1M</span>

        <div className="macd-divider" />

        {/* Legend */}
        <div className="macd-legend">
          <span className="macd-legend-item macd-line-color">
            MACD&nbsp;<strong>{latestMacd != null ? latestMacd.toFixed(2) : '—'}</strong>
          </span>
          <span className={`macd-signal-badge ${signal.cls}`}>{signal.label}</span>
          <span className="macd-level-item macd-zero-color">── 0</span>
          {macdLoading && <span className="macd-loading-dot" />}
        </div>
      </div>

      {/* ── Chart area ────────────────────────────────────────────────────── */}
      <div className="macd-charts-area">

        {loading && (
          <div className="chart-overlay">
            <div className="spinner" />
            <span>Loading…</span>
          </div>
        )}
        {error && !loading && (
          <div className="chart-overlay error">
            <span>⚠️ {error}</span>
          </div>
        )}

        {/* Price chart — 60% */}
        <div className="macd-price-wrapper">
          <div className="macd-sub-label">Price (Daily)</div>
          <div ref={priceContainerRef} className="macd-chart-container" />
        </div>

        {/* MACD sub-chart — 40% */}
        <div className="macd-sub-wrapper">
          <div className="macd-sub-label">MACD Line (EMA 12 − EMA 26)</div>
          <div ref={macdContainerRef} className="macd-chart-container" />
        </div>

      </div>
    </div>
  );
}

export default MacdDashboard;

